import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Panel from '@/components/panel'
import { colors, breakpoints } from '@/styles'
import { videoHasProcessed } from '@/utils/video'
import VideoFileUpload, { useProcessing } from './video-upload'
import VideoLink from './video-link'

const VideoLimitNote = styled.p`
  font-size: 16px;
  line-height: 22px;
  color: ${colors.regular};
  text-align: center;
  margin: 10px auto 30px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 15px auto 20px;
  }
`

const Wrapper = styled(Panel)`
  border: 1px dashed #a5a5a5;
  display: flex;
  flex-wrap: wrap;
  padding: 20px 28px 0px;
  outline: none;
  gap: 20px 30px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 10px 10px;
    padding: 15px 15px 0 15px;
  }
`

const VideoProcessingNote = styled.p`
  font-size: 14px;
  color: ${colors.regular};
  text-align: center;
  margin: 0 auto 30px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin-bottom: 20px;
  }
`

const Notes = styled.div`
  width: 100%;
`

const VideoUpload = ({
  videoFiles,
  unitId,
  listingId,
  uploadInProgress,
  videoLinks = {},
  onFileUploadStart,
  onFileUploadEnd,
  onFileUploadProgress,
  onFileUploadError,
  onVideoLinkAddSuccess
}) => {
  const { t } = useTranslation()
  const [processing, setProcessing] = useProcessing(5500, uploadInProgress, videoFiles)
  const showProcessingMessage = videoFiles?.length && !videoHasProcessed(videoFiles[0].created_at)

  //don't allow file upload if video link exists. If user already has video file and link somehow, show them both
  const allowFileUpload = !videoLinks?.tour_video?.link_url || videoFiles?.length

  //hide 2d video link if user has video file. Same as above, if they already have both somehow, show both
  const allow2DLinkAttachment =
    !(videoFiles?.length || uploadInProgress || processing) || videoLinks?.tour_video?.link_url

  const onUploadStart = () => {
    onFileUploadStart()
    setProcessing(true)
  }
  const onUploadError = () => {
    onFileUploadError()
    setProcessing(false)
  }

  return (
    <Wrapper>
      {allowFileUpload ? (
        <VideoFileUpload
          items={videoFiles}
          unitId={unitId}
          listingId={listingId}
          inProgress={uploadInProgress}
          onUploadStart={onUploadStart}
          onUploadEnd={onFileUploadEnd}
          onUploadProgress={onFileUploadProgress}
          onUploadError={onUploadError}
        />
      ) : null}
      {allow2DLinkAttachment ? (
        <VideoLink
          onVideoLinkAddSuccess={onVideoLinkAddSuccess}
          listingId={listingId}
          linkData={videoLinks?.tour_video}
          videoType="2d"
        />
      ) : null}
      <VideoLink
        onVideoLinkAddSuccess={onVideoLinkAddSuccess}
        listingId={listingId}
        linkData={videoLinks?.tour_3d}
        videoType="3d"
      />
      <Notes>
        <VideoLimitNote>
          {t('l.listing.edit.video_limit.text', 'Maximum of 1 video and 1 3D link allowed.')}
        </VideoLimitNote>
        {showProcessingMessage ? (
          <VideoProcessingNote>
            {t(
              'b.listing.view.video_processing.text',
              'Recently uploaded videos may take up to 10 minutes to be processed and displayed'
            )}
            .
          </VideoProcessingNote>
        ) : null}
      </Notes>
    </Wrapper>
  )
}

export default VideoUpload
